'use client';

import Text, { TEXT_VARIANT } from '@pixum/text';
import { Dialog, DialogContent, DialogHeader } from '@pixum/web-dialog';
import type { ImageNode } from 'data-layer';
import { Activity } from 'react';
import { Image } from '../../../image/Image';
import { RichTextBody } from '../../../richtText/RichTextBody';
import styles from './OrderStatusDialog.module.css';
import type { OrderStatusDialogProps } from './types';

/**
 * OrderStatusDialog component - displays help/information dialog
 * Client Component - uses Pixum design system Dialog component
 *
 * Features:
 * - Portal-based rendering for proper z-index stacking
 * - Built-in escape key handling via useEscapeKeyPress hook
 * - Backdrop click to close
 * - Scroll prevention when open via usePreventScroll hook
 * - Full ARIA support (aria-modal, role, aria-label)
 * - Close button with accessibility
 * - Renders rich text content via RichTextBody component
 * - Displays media images when available
 */
export function OrderStatusDialog({ isOpen, onClose, dialog, device }: OrderStatusDialogProps) {
  const hasImage = !!dialog.image;

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      showCloseButton
      header={<DialogHeader center={<Text variant={TEXT_VARIANT.TITLE_3}>{dialog.headline}</Text>} right={null} />}
      content={
        <DialogContent className={styles.dialogContent}>
          <>
            <RichTextBody wrapWithText={false} text={dialog.description} />
            <Activity mode={hasImage ? 'visible' : 'hidden'}>
              <div className={styles.imageContainer}>
                <Image
                  className={styles.image}
                  device={device}
                  loading="eager"
                  image={dialog.image as ImageNode}
                ></Image>
              </div>
            </Activity>
          </>
        </DialogContent>
      }
    />
  );
}
